import React from 'react';

import styled from 'styled-components';

import { isCommunityCCRC } from 'components/Community/utils';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { VALUE_DIFFERENCE_DISPLAY } from 'components/View/constants';

import { PRICING_COMPARISON_COLUMN_WIDTH } from './constants';
import { PricingComparisonRow } from './types';
import { formatCurrency, formatText, getDisplayDifference } from './utils';
import { WIDGET_TYPES } from '../constants';
import { formatValueDifference, getDifferenceColor, shouldDisplayValueDifference } from '../valueDifferenceUtils';

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  border: 1px solid #ddd;
  min-width: 700px;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--line-gray);
`;

const DataRow = styled(TableRow)`
  height: 60px;
`;

const TableCell = styled.td`
  padding: 0 16px;
  font-size: 14px;
  color: var(--text-primary);
  white-space: nowrap;
  text-align: left;
  vertical-align: middle;
  height: 60px;
`;

const CareTypeCell = styled(TableCell)`
  font-size: 14px;
  font-family: var(--font-bold);
`;

const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DifferenceText = styled.span<{ color: string }>`
  font-size: 12px;
  color: ${({ color }) => color};
`;

type Props = {
  dataSource: PricingComparisonRow[];
  roomTypes: { id: number; name: string }[];
  columnCount: number;
  minTableWidth: number;
  comparisonTypeEnabled?: boolean;
  valueDisplay?: number;
};

export const PricingComparisonTableBody = ({
  dataSource,
  roomTypes,
  columnCount,
  minTableWidth,
  comparisonTypeEnabled,
  valueDisplay,
}: Props) => {
  const { community } = useCommunityViewContext();
  const isCCRC = isCommunityCCRC(community?.rental_types ?? []);
  const canShowDifferences = shouldDisplayValueDifference(comparisonTypeEnabled, valueDisplay);
  const displayMode = valueDisplay ?? VALUE_DIFFERENCE_DISPLAY.AMOUNTS;
  return (
    <DataTable style={{ minWidth: `${minTableWidth}px` }} data-testid="pc-widget-body-table">
      <colgroup>
        {Array.from({ length: columnCount }).map((_, index) => (
          <col
            key={`data-col-${index}`}
            style={{ width: `${PRICING_COMPARISON_COLUMN_WIDTH + (index === 0 ? 30 : 0)}px` }}
          />
        ))}
      </colgroup>
      <tbody>
        {dataSource.map((row) => (
          <DataRow key={row.key} data-testid={`pc-widget-row-${row.key}`}>
            <CareTypeCell data-testid={`pc-widget-cell-care-type-${row.key}`}>{formatText(row.careType)}</CareTypeCell>
            {roomTypes.map((roomType) => (
              <TableCell
                key={`${row.key}-${roomType.id}`}
                data-testid={`pc-widget-cell-room-type-${row.key}-${roomType.id}`}
              >
                <CellStack>
                  <span>{formatCurrency(row.roomTypeMap[roomType.id])}</span>
                  {canShowDifferences &&
                    formatValueDifference(
                      getDisplayDifference(
                        row.roomTypeMap[roomType.id],
                        row.roomTypeDifferenceMap[roomType.id],
                        displayMode
                      ),
                      WIDGET_TYPES.PRICING_COMPARISON,
                      displayMode
                    ) && (
                      <DifferenceText
                        color={getDifferenceColor(row.roomTypeDifferenceMap[roomType.id] ?? 0)}
                        data-testid={`pc-widget-difference-room-type-${row.key}-${roomType.id}`}
                      >
                        {formatValueDifference(
                          getDisplayDifference(
                            row.roomTypeMap[roomType.id],
                            row.roomTypeDifferenceMap[roomType.id],
                            displayMode
                          ),
                          WIDGET_TYPES.PRICING_COMPARISON,
                          displayMode
                        )}
                      </DifferenceText>
                    )}
                </CellStack>
              </TableCell>
            ))}
            {isCCRC ? (
              <TableCell data-testid={`pc-widget-cell-entrance-fee-${row.key}`}>
                <CellStack>
                  <span>{formatCurrency(row.entranceFee)}</span>
                  {canShowDifferences &&
                    formatValueDifference(
                      getDisplayDifference(row.entranceFee, row.entranceFeeDifference, displayMode),
                      WIDGET_TYPES.PRICING_COMPARISON,
                      displayMode
                    ) && (
                      <DifferenceText
                        color={getDifferenceColor(row.entranceFeeDifference ?? 0)}
                        data-testid={`pc-widget-difference-entrance-fee-${row.key}`}
                      >
                        {formatValueDifference(
                          getDisplayDifference(row.entranceFee, row.entranceFeeDifference, displayMode),
                          WIDGET_TYPES.PRICING_COMPARISON,
                          displayMode
                        )}
                      </DifferenceText>
                    )}
                </CellStack>
              </TableCell>
            ) : (
              <TableCell data-testid={`pc-widget-cell-community-fee-${row.key}`}>
                <CellStack>
                  <span>{formatCurrency(row.communityFee)}</span>
                  {canShowDifferences &&
                    formatValueDifference(
                      getDisplayDifference(row.communityFee, row.communityFeeDifference, displayMode),
                      WIDGET_TYPES.PRICING_COMPARISON,
                      displayMode
                    ) && (
                      <DifferenceText
                        color={getDifferenceColor(row.communityFeeDifference ?? 0)}
                        data-testid={`pc-widget-difference-community-fee-${row.key}`}
                      >
                        {formatValueDifference(
                          getDisplayDifference(row.communityFee, row.communityFeeDifference, displayMode),
                          WIDGET_TYPES.PRICING_COMPARISON,
                          displayMode
                        )}
                      </DifferenceText>
                    )}
                </CellStack>
              </TableCell>
            )}

            <TableCell data-testid={`pc-widget-cell-fee-type-${row.key}`}>{formatText(row.feeType)}</TableCell>
            <TableCell data-testid={`pc-widget-cell-care-fee-${row.key}`}>
              <CellStack>
                <span>{formatCurrency(row.careFee)}</span>
                {canShowDifferences &&
                  formatValueDifference(
                    getDisplayDifference(row.careFee, row.careFeeDifference, displayMode),
                    WIDGET_TYPES.PRICING_COMPARISON,
                    displayMode
                  ) && (
                    <DifferenceText
                      color={getDifferenceColor(row.careFeeDifference ?? 0)}
                      data-testid={`pc-widget-difference-care-fee-${row.key}`}
                    >
                      {formatValueDifference(
                        getDisplayDifference(row.careFee, row.careFeeDifference, displayMode),
                        WIDGET_TYPES.PRICING_COMPARISON,
                        displayMode
                      )}
                    </DifferenceText>
                  )}
              </CellStack>
            </TableCell>
            {isCCRC && (
              <TableCell data-testid={`pc-widget-cell-contract-type-${row.key}`}>
                {formatText(row.contractLabel)}
              </TableCell>
            )}
          </DataRow>
        ))}
      </tbody>
    </DataTable>
  );
};

export default PricingComparisonTableBody;
